import { cloneElement, ReactNode, useRef } from 'react'
import { mergeProps, useButton } from 'react-aria'
import {
	OverlayArrow,
	Tooltip as AriaTooltip,
	TooltipTrigger,
	TooltipTriggerComponentProps,
	TooltipProps,
} from 'react-aria-components'

type Props = TooltipTriggerComponentProps &
	Pick<TooltipProps, 'placement'> & {
		className?: string
		content: ReactNode
		children: ReactNode
	}

const styles = {
	base: 'group bg-slate-800 border border-slate-900 text-gray-100 text-sm rounded-md shadow-lg will-change-transform p-2',
	isEntering: 'animate-in fade-in ease-out duration-200',
	isExiting: 'animate-out fade-out ease-in duration-150',
} as const

export function Tooltip({ content, children, placement, className = '', ...props }: Props) {
	return (
		<TooltipTrigger {...props}>
			{children}
			<AriaTooltip
				offset={4}
				className={({ isEntering, isExiting }) =>
					`pointer-events-none ${className} ${styles.base} ${isEntering ? styles.isEntering : ''} ${isExiting ? styles.isExiting : ''}`
				}
				placement={placement}
			>
				<OverlayArrow>
					<svg
						width={8}
						height={8}
						viewBox="0 0 8 8"
						className="fill-slate-700 stroke-gray-800 group-placement-bottom:rotate-180 group-placement-left:-rotate-90 group-placement-right:rotate-90"
					>
						<path d="M0 0 L4 4 L8 0" />
					</svg>
				</OverlayArrow>
				{content}
			</AriaTooltip>
		</TooltipTrigger>
	)
}

export function TooltipWrap(props: { children: JSX.Element }) {
	const triggerRef = useRef<HTMLDivElement>(null)
	const { buttonProps } = useButton({}, triggerRef)

	return cloneElement(
		props.children,
		mergeProps(buttonProps, props.children.props, { ref: triggerRef }),
	)
}
